<script>
    // Notes:
    // - watch issues (see https://github.com/vuejs/petite-vue/discussions/49). Ideally to solve the following issues we would always use mounted to getItems and watch for page, sortBy and sortDirection to re-get the items:
    //   - Filtering when using v-effect="getItems() has a hack to reset to the first page.
    //     - To remove the hack remove all instances of pageChanging and possibly move the onPageChanged function to the mixin.
    //   - Sort doesn't work when getting the items when the component is mounted. Works with v-effect="getItems()", however this means the items are updated every time the filter is updated, this might not be ideal as causes multiple Ajax requests.
    //   - Currently we have the following code in certain modules:
    //     list.$data.filter.url = url;
    //     list.$data.find();
    //
    //     the problem with this is that v-effect causes getItems to fire as soon as the filter changes, therefore when calling find it causes the API to ge called twice.
    //   - If we change so the filter no longer auto triggers getItems we need to update the shop filter so when changing the checkbox value it auto gets the items.

    import listMixin, { isObject, isValid } from './list-mixin.js';
    import Toast from 'bootstrap.toast';
    import { displayValidationSummary } from 'ui-utils';
    import { fetch, parseErrors } from 'utils';

    export default function(props = {}) {
        const root = this;
        let hasLoaded = false, pageChanging;

        return {
            loaded: false, // This must be set before merging the mixin as it allows you to override it in the data property.
            loading: false,
            ...listMixin(props),
            apiUrl: props.apiUrl,
            headers: props.headers ?? {},
            useFormData: props.useFormData ?? true,
            pageKey: props.pageKey ?? 'page',
            pageSizeKey: props.pageSizeKey ?? 'pageSize',
            defaultPageSize: props.defaultPageSize,
            // The page size is optional so we can use the default value in the API.
            pageSize: props.pageSize,
            totalPages: props.totalPages ?? 1,
            defaultSortBy: props.defaultSortBy,
            sortByKey: props.sortByKey ?? 'sortBy',
            sortBy: props.sortBy ?? 'Name',
            defaultSortDirection: props.defaultSortDirection,
            sortDirectionKey: props.sortDirectionKey ?? 'sortDirection',
            successMessage: props.successMessage ?? 'Items successfully updated.',
            errorMessage: props.successMessage ?? 'There was a problem processing your request.',
            // Exposing the root instance allows you to access the root data within this component.
            get $root() {
                return root;
            },
            mounted(element, data = this) {
                // Call the root mounted method.
                root.mounted(element, data);
            },
            unmounted(element, data = this) {
                // Call the root unmounted method.
                root.unmounted(element, data);
            },
            onPageChanged(e) {
                pageChanging = true;

                // Set the page.
                this.page = e.detail;
            },
            async find() {
                // Set the page to the first one.
                this.page = 1;

                // Get the items.
                await this.getItems();
            },
            async getItems() {
                // Make sure the apiUrl has been specified.
                if (this.apiUrl === undefined)
                    throw 'You must specify the "api-url" attribute.';

                // Build up the query string.
                const queryString = {};

                // Add the filter parameters.
                for (const key in this.filter) {
                    queryString[key] = this.filter[key];
                }

                // Add the paging parameters (if applicable).
                if (this.enablePaging) {
                    // Make sure the page is reset unless we are actually changing the page.
                    if (pageChanging === false)
                        this.page = 1;

                    pageChanging = false;

                    if (this.page > 1)
                        queryString[this.pageKey] = this.page;

                    if (this.pageSize !== this.defaultPageSize)
                        queryString[this.pageSizeKey] = this.pageSize;
                }

                // Add the sorting parameters (if applicable).
                if (this.enableSorting) {
                    if (this.sortBy != this.defaultSortBy)
                        queryString[this.sortByKey] = this.sortBy;

                    if (this.sortDirection != this.defaultSortDirection)
                        queryString[this.sortDirectionKey] = this.sortDirection;
                }

                // Make sure the data has not already been set as loaded. This allows you to set the initial state without re-loading the data.
                // Note: This needs to go here so that vue can set the state that this function is re-active to.
                if (this.loaded && !hasLoaded) {
                    hasLoaded = true;
                    return;
                }

                // Work out the full URL.
                const url = getUrl(this.apiUrl, queryString);

                // Trigger the loading event.
                const cancelled = !this.$refs.root.dispatchEvent(new CustomEvent('list:loading', { bubbles: this.eventBubbles, cancelable: true, detail: { list: this, url } }));

                // Get the data if the default has not been prevented.
                if (!cancelled) {
                    this.loading = true;

                    const response = await fetch(url, {
                        headers: this.headers
                    });

                    this.loading = false; // This needs to be set before firing the list:loaded event, so the view is updated.

                    if (response.ok) {
                        const data = await response.json();

                        // Make sure the data contains a property with the items key.
                        if (!(this.itemsKey in data))
                            throw `The data object does not contain a property with the key "${this.itemsKey}".`;

                        // Set the total pages and items.
                        this.totalPages = data.totalPages;
                        this[this.itemsKey] = data[this.itemsKey];

                        // Set that the data has been loaded.
                        this.loaded = true;

                        // Trigger the loaded event.
                        this.$refs.root.dispatchEvent(new CustomEvent('list:loaded', { bubbles: this.eventBubbles, detail: { list: this, url, data } }));

                        // Trigger the view updated event. Wrapping in $nextTick will make sure the view has updated/mounted.
                        this.$nextTick(() => this.$refs.root.dispatchEvent(new CustomEvent('list:loaded:viewUpdated', { bubbles: this.eventBubbles, detail: { list: this, url, data } })));
                    }
                }
            },
            async add(item, e = event) {
                // Validate the form.
                if (!isValid(e.target))
                    return;

                // Trigger the adding event.
                const cancelled = !this.$refs.root.dispatchEvent(new CustomEvent('list:adding', { bubbles: this.eventBubbles, cancelable: true, detail: { list: this, item, event: e } }));

                // Add the item if the default has not been prevented.
                if (!cancelled) {
                    // Try to add the item.
                    const response = await fetch(this.apiUrl, {
                        method: 'POST',
                        headers: !this.useFormData ? {
                            ...{
                                'Content-Type': 'application/json'
                            },
                            ...this.headers
                        } : this.headers,
                        body: !this.useFormData ? JSON.stringify(item) : getFormData(item)
                    });

                    // Handle the response.
                    await handleResponse(this, e.target.closest('form') ?? this.$refs.root, response);

                    if (response.ok) {
                        // Get the items.
                        await this.getItems();

                        // Trigger the added event.
                        this.$refs.root.dispatchEvent(new CustomEvent('list:added', { bubbles: this.eventBubbles, detail: { list: this, item, event: e } }));

                        // Reset the form. Make sure it's after the added event, so the item passed to into the event is not reset, alternatively we could create a clone of the item.
                        this.reset(item, e);
                    }
                }
            },
            async update(item, e = event) {
                // Validate the form.
                if (!isValid(e.target))
                    return;

                // Trigger the updating event.
                const cancelled = !this.$refs.root.dispatchEvent(new CustomEvent('list:updating', { bubbles: this.eventBubbles, cancelable: true, detail: { list: this, item, event: e } }));

                // Update the item if the default has not been prevented.
                if (!cancelled) {
                    // Try to update the item.
                    const response = await fetch(this.apiUrl + '/' + item.id, {
                        method: 'PUT',
                        headers: !this.useFormData ? {
                            ...{
                                'Content-Type': 'application/json'
                            },
                            ...this.headers
                        } : this.headers,
                        body: !this.useFormData ? JSON.stringify(item) : getFormData(item)
                    });

                    // Handle the response.
                    handleResponse(this, e.target.closest('.edit') ?? e.target.closest('form') ?? this.$refs.root, response);

                    if (response.ok) {
                        // Trigger the updated event.
                        this.$refs.root.dispatchEvent(new CustomEvent('list:updated', { bubbles: this.eventBubbles, detail: { list: this, item, event: e } }));

                        // Set the item we are editing to none.
                        this[this.editItemKey] = null;

                        // Trigger the view updated event. Wrapping in $nextTick will make sure it has switched to the item template.
                        this.$nextTick(() => this.$refs.root.dispatchEvent(new CustomEvent('list:updated:viewUpdated', { bubbles: this.eventBubbles, detail: { list: this, item, event: e } })));
                    }
                }
            },
            async remove(item, e = event) {
                // Trigger the removing event.
                const cancelled = !this.$refs.root.dispatchEvent(new CustomEvent('list:removing', { bubbles: this.eventBubbles, cancelable: true, detail: { list: this, item, event: e } }));

                // Update the item if the default has not been prevented.
                if (!cancelled) {
                    // Try to delete the item.
                    const response = await fetch(this.apiUrl + '/' + item.id, {
                        method: 'DELETE',
                        headers: this.headers
                    });

                    // Handle the response.
                    handleResponse(this, e.target.closest('.view') ?? e.target.closest('form') ?? this.$refs.root, response);

                    if (response.ok) {
                        // Get the items.
                        await this.getItems();

                        // Trigger the removed event.
                        this.$refs.root.dispatchEvent(new CustomEvent('list:removed', { bubbles: this.eventBubbles, detail: { list: this, item, event: e } }));
                    }
                }
            }
        };
    }

    export function getFormData(item) {
        const formData = new FormData();

        for (const key in item) {
            const value = item[key];

            // Handling arrays allows you to add multiple files.
            if (Array.isArray(value))
                value.forEach(v => formData.append(key, v));
            // Handling objects allows for complex types, e.g. product. Also make sure it's not a File otherwise file uploads won't work.
            else if (isObject(value) && !(value instanceof File))
                Object.keys(value).forEach(k => formData.append(`${key}.${k}`, value[k]));
            else
                formData.append(key, value);
        }

        return formData;
    }

    export function getUrl(url, queryString = {}) {
        const qs = Object.keys(queryString).filter(k => queryString[k] !== null).flatMap(k => {
            if (Array.isArray(queryString[k]))
                return queryString[k]
                    .filter(v => v !== null)
                    .map(v => [k, v].map(encodeURIComponent).join('='));
            else
                return [k, queryString[k]].map(encodeURIComponent).join('=');
        });

        return url + (qs.length ? (url.includes('?') ? '&' : '?') + qs.join('&') : '');
    }

    // Note: The container is where it should look for the validation summary. This prevents a possible issue where the add validation summary appears when editing something.
    export async function handleResponse(list, container, response) {
        // Get the response data.
        const contentType = response.headers.get('content-type');
        const isJson = contentType && contentType.includes('json');
        const data = isJson ? await response.json() : await response.text();

        if (response.ok) {
            // Get the success message.
            let successMessage;

            if (typeof data === 'object' && 'message' in data)
                successMessage = data.message;
            else if (typeof data === 'string' && data != '')
                successMessage = data;
            else
                successMessage = list.successMessage;

            // Set the success message.
            list.successMessage = successMessage;

            // Trigger the success event.
            const cancelled = !list.$refs.root.dispatchEvent(new CustomEvent('list:success', { bubbles: list.eventBubbles, cancelable: true, detail: { list, container, response, data, message: successMessage } }));

            if (!cancelled) {
                const success = list.$refs.root.querySelector('.success');

                if (success != null)
                    Toast.getOrCreateInstance(success).show();
            }
        } else {
            // Get the errors.
            const errors = parseErrors(data, list.errorMessage);

            // Get the error message.
            const errorMessage = errors.join(', ');

            // Set the error message.
            list.errorMessage = errorMessage;

            // Trigger the failure event.
            const cancelled = !list.$refs.root.dispatchEvent(new CustomEvent('list:failure', { bubbles: list.eventBubbles, cancelable: true, detail: { list, container, response, data, message: errorMessage, errors } }));

            if (!cancelled) {
                const failure = list.$refs.root.querySelector('.failure');

                if (failure != null)
                    Toast.getOrCreateInstance(failure).show();
                else
                    displayValidationSummary(container, errors);
            }
        }
    }
</script>